import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AcademyOwl from "../assets/authLogo.png";
import styles from "./navbar.module.scss";

const ACADEMY_URL = "https://academy.traingrc.com/p/home";
const LOGIN_URL =
  "https://sso.teachable.com/secure/1110292/identity/login/password";

const NavBar = ({ active }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const firstMobileLinkRef = useRef(null);
  const mobileNavRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    firstMobileLinkRef.current?.focus();

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key === "Tab") {
        const focusable = Array.from(
          mobileNavRef.current?.querySelectorAll("a[href], button:not([disabled])") || []
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  const links = (
    <>
      <a href={ACADEMY_URL}>Academy</a>
      <Link to="/solutions" aria-current={active === "solutions" ? "page" : undefined}>
        Solutions
      </Link>
      <Link to="/about-us" aria-current={active === "AboutUs" ? "page" : undefined}>
        About us
      </Link>
      <Link to="/glossary" aria-current={active === "glossary" ? "page" : undefined}>
        Glossary
      </Link>
    </>
  );

  return (
    <header className={styles.siteHeader}>
      <a className={styles.skipLink} href="#main-content">
        Skip to main content
      </a>
      <div className={styles.navbar}>
        <Link className={styles.brand} to="/" aria-label="Train GRC home">
          <img src={AcademyOwl} alt="" width="60" height="68" />
          <span>
            <strong>Train GRC</strong>
            <small>Academy · Studio · Advisory</small>
          </span>
        </Link>

        <nav className={styles.desktopNav} aria-label="Primary navigation">
          {links}
        </nav>

        <div className={styles.desktopActions}>
          <a className={styles.loginLink} href={LOGIN_URL}>
            Login
          </a>
          <Link className={styles.signupLink} to="/signup">
            Start learning
          </Link>
        </div>

        <button
          ref={menuButtonRef}
          className={styles.menuButton}
          type="button"
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
          aria-label={isOpen ? "Close navigation" : "Open navigation"}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </div>

      {isOpen && (
        <div
          className={styles.mobileLayer}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <button
            className={styles.backdrop}
            type="button"
            aria-label="Close navigation"
            onClick={closeMenu}
          />
          <nav
            ref={mobileNavRef}
            id="mobile-navigation"
            className={styles.mobileNav}
            aria-label="Mobile navigation"
          >
            <div className={styles.mobileLinks} onClick={closeMenu}>
              <a ref={firstMobileLinkRef} href={ACADEMY_URL}>
                Academy
              </a>
              <Link to="/solutions">Solutions</Link>
              <Link to="/about-us">About us</Link>
              <Link to="/glossary">Glossary</Link>
            </div>
            <div className={styles.mobileActions}>
              <a className={styles.loginLink} href={LOGIN_URL}>
                Academy login
              </a>
              <Link className={styles.signupLink} to="/signup">
                Start learning
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default NavBar;
